import DatePicker from 'react-datepicker';
import { format, parse, isValid } from 'date-fns';
import { clsx } from 'clsx';
import type { Region } from '@court-sync/shared';

const ISO_FORMAT = 'yyyy-MM-dd';
const US_DISPLAY_FORMAT = 'MM/dd/yyyy';
const AU_DISPLAY_FORMAT = 'dd/MM/yyyy';

const US_PLACEHOLDER = 'MM/DD/YYYY';
const AU_PLACEHOLDER = 'DD/MM/YYYY';

function getDateFormat(region: Region | undefined | null): string {
  return region === 'AU' ? AU_DISPLAY_FORMAT : US_DISPLAY_FORMAT;
}

function getPlaceholder(region: Region | undefined | null): string {
  return region === 'AU' ? AU_PLACEHOLDER : US_PLACEHOLDER;
}

function isoToDate(iso: string): Date | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = parse(iso, ISO_FORMAT, new Date());
  return isValid(d) ? d : null;
}

export interface RegionDateInputProps {
  /** Value in yyyy-MM-dd (ISO) format */
  value: string;
  /** Called with yyyy-MM-dd when user selects a date */
  onChange: (value: string) => void;
  /** Academy region: US shows MM/DD/YYYY, AU shows DD/MM/YYYY */
  region?: Region | null;
  error?: boolean;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
}

/**
 * Date input with calendar picker that displays dates according to academy region.
 * US: MM/DD/YYYY, AU: DD/MM/YYYY. Internal value is always yyyy-MM-dd.
 */
export function RegionDateInput({
  value,
  onChange,
  region = 'US',
  className,
  error,
  placeholder,
  disabled,
  id,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
}: RegionDateInputProps) {
  const dateFormat = getDateFormat(region);
  const placeholderText = placeholder ?? getPlaceholder(region);
  const selected = isoToDate(value);

  const handleChange = (date: Date | null) => {
    onChange(date ? format(date, ISO_FORMAT) : '');
  };

  return (
    <div className="w-full [&_.react-datepicker-wrapper]:block [&_.react-datepicker-wrapper]:w-full [&_.react-datepicker__input-container]:block [&_.react-datepicker__input-container]:w-full">
      <DatePicker
        selected={selected}
        onChange={handleChange}
        dateFormat={dateFormat}
        placeholderText={placeholderText}
        disabled={disabled}
        id={id}
        ariaLabel={ariaLabel}
        ariaDescribedBy={ariaDescribedBy}
        className={clsx(
          'input w-full box-border',
          error && 'border-red-500 focus-visible:ring-red-500',
          className
        )}
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
      />
    </div>
  );
}
